import { GrowthBook } from "@growthbook/growthbook";
import type { GrowthbookFeatures } from "@shared/growthbook";
import { type App, computed, inject, type Ref, ref } from "vue";

import { useSegment } from "./segment";

type GrowthbookPlugin = {
  features: Ref<GrowthBook<GrowthbookFeatures> | undefined>;
  loaded: Ref<boolean>;
  errored: Ref<boolean>;
  identify: (userId?: string | null) => Promise<void>;
};

const growthbookClientKey = import.meta.env.VITE_GROWTHBOOK_KEY;
const isDevMode = import.meta.env.DEV && !import.meta.env.VITE_GROWTHBOOK_KEY;

export default {
  install: async (app: App) => {
    const growthbookLoaded = ref(false);
    const growthbookErrored = ref(false);
    const growthbookFeatures = ref<GrowthBook<GrowthbookFeatures> | undefined>(
      undefined,
    );

    growthbookFeatures.value = new GrowthBook({
      apiHost: "https://cdn.growthbook.io",
      clientKey: growthbookClientKey,
      enableDevMode: isDevMode,
      attributes: {
        userAgent: navigator.userAgent,
      },
    });

    const identify = async (userId?: string | null) => {
      try {
        await growthbookFeatures.value?.updateAttributes({
          // Check if the user id is set, otherwise use the anonymous id from segment
          id: userId,
        });

        await growthbookFeatures.value?.loadFeatures({ autoRefresh: true });

        growthbookLoaded.value = true;
        growthbookErrored.value = false;
      } catch {
        growthbookLoaded.value = false;
        growthbookErrored.value = true;
      }
    };

    return app.provide("growthbook", {
      features: growthbookFeatures,
      loaded: growthbookLoaded,
      errored: growthbookErrored,
      identify,
    });
  },
  afterInstall: async () => {
    const $segment = useSegment();
    const $growthbook = useGrowthbook();

    // Check if the user id is set, otherwise use the anonymous id from segment
    const growthbookUserId =
      $segment.user().id() || $segment.user().anonymousId();

    await $growthbook.identify(growthbookUserId);

    // Set tracking callback after segment is already loaded
    $growthbook.features.value?.setTrackingCallback((experiment, result) => {
      $segment.oldTrack("Experiment Viewed", {
        experimentId: experiment.key,
        variationId: result.key,
      });
    });
  },
};

export const useGrowthbook = () => {
  const resolved = inject<GrowthbookPlugin>("growthbook");

  if (!resolved) {
    throw new Error("Tried to use growthbook before it was loaded");
  }

  return resolved;
};

export const useSplitTest = <T extends keyof GrowthbookFeatures>(
  test: T,
  fallback: GrowthbookFeatures[T],
) => {
  const $growthbook = useGrowthbook();

  // Make result computed so we only call the tracking callback whenever the value gets used.
  const result = computed(() => {
    const feature = $growthbook.features.value?.evalFeature(test)?.value;

    return feature ?? fallback;
  });

  return computed(() => {
    if ($growthbook.loaded.value) {
      return {
        result: result,
        loaded: true,
      } as const;
    }

    if ($growthbook.errored.value) {
      return {
        result: ref(fallback),
        loaded: true,
      } as const;
    }

    return {
      result: ref(undefined),
      loaded: false,
    } as const;
  });
};

export const useFeatureFlag = <
  T extends keyof GrowthbookFeatures,
  R extends GrowthbookFeatures[T],
>(
  flag: T,
  defaultValue: R,
) => {
  const $growthbook = useGrowthbook();

  // Make result computed so we only call the tracking callback whenever the value gets used.
  const result = computed(() => {
    const feature = $growthbook.features.value?.isOn(flag);

    return feature ?? defaultValue;
  });

  return computed(() => {
    if ($growthbook.loaded.value) {
      return {
        result: result,
        loaded: true,
      } as const;
    }

    if ($growthbook.errored.value) {
      return {
        result: ref(defaultValue),
        loaded: true,
      } as const;
    }

    return {
      result: ref(undefined),
      loaded: false,
    } as const;
  });
};
